import React from 'react';
import { acceptInvitation, checkLink, resetPassword } from './customerSession';
import * as s from './portalStyles';
import PortalBrand from './PortalBrand';

// Hand-duplicated from PASSWORD_MIN_LENGTH in packages/shared: apps/web cannot import that package
// (ADR 0019). The API enforces the real rule — this only avoids a pointless round trip.
const PASSWORD_MIN_LENGTH = 12;

const COPY = {
  invite: {
    heading: 'Set your password',
    intro: 'Choose a password to finish setting up your account.',
    submit: 'Set password and continue',
    done: 'Your account is ready.',
    invalid:
      "This invitation link is no longer valid — it may have expired, or it may already have been used.",
  },
  reset: {
    heading: 'Choose a new password',
    intro: 'Pick a new password for your account.',
    submit: 'Save new password',
    done: 'Your password has been changed.',
    invalid:
      'This password reset link is no longer valid — it may have expired, or it may already have been used.',
  },
};

/// Serves both /portal/set-password (a new contact accepting an invitation) and
/// /portal/reset-password. They differ only in wording and which token purpose is spent, so one
/// component with a `kind` beats two near-identical files.
export default function SetPasswordScreen({ kind, token, onDone, onRequestNewLink }) {
  const copy = COPY[kind];
  const [state, setState] = React.useState({ status: 'checking' });
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    checkLink(kind, token).then((result) => {
      if (cancelled) return;
      setState(result.valid ? { status: 'ready', ...result } : { status: 'invalid' });
    });
    return () => {
      cancelled = true;
    };
  }, [kind, token]);

  async function handleSubmit(event) {
    event.preventDefault();
    // Checked here rather than only server-side: a mistyped confirmation would otherwise spend the
    // single-use token on a password the person didn't mean to set.
    if (password !== confirm) {
      setError("Those passwords don't match.");
      return;
    }
    setError('');
    setBusy(true);
    try {
      if (kind === 'invite') await acceptInvitation(token, password);
      else await resetPassword(token, password);
      setState({ status: 'done' });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={s.page}>
      <PortalBrand />
      <div style={s.subtitle}>Aumentum Technologies · Customer Portal</div>

      {state.status === 'checking' && <div style={s.helpText}>Checking your link…</div>}

      {state.status === 'invalid' && (
        <>
          <div style={s.heading}>This link has expired</div>
          <div style={s.helpText}>
            {copy.invalid} You can request a new one — it'll be sent to the same address.
          </div>
          <button className="btn btn-primary" style={s.submitButton} onClick={onRequestNewLink}>
            Request a new link
          </button>
        </>
      )}

      {state.status === 'done' && (
        <>
          <div style={s.heading}>{copy.done}</div>
          <div style={s.helpText}>You can now sign in with your new password.</div>
          <button className="btn btn-primary" style={s.submitButton} onClick={onDone}>
            Go to sign in
          </button>
        </>
      )}

      {state.status === 'ready' && (
        <>
          <div style={s.heading}>{copy.heading}</div>
          <div style={s.helpText}>
            {state.name ? `Hello ${state.name}. ` : ''}
            {copy.intro} Signing in as <strong>{state.email}</strong>.
          </div>
          {error && <div style={s.errorBox}>⚠ {error}</div>}
          <form onSubmit={handleSubmit}>
            {/* Hidden but present, so password managers know which account the saved credential
                belongs to — without it they tend to save the password against no username. */}
            <input type="hidden" name="username" autoComplete="username" value={state.email} readOnly />
            <div className="form-group">
              <label className="form-label" htmlFor="new-password">New password</label>
              <input
                id="new-password"
                className="form-input"
                type="password"
                autoComplete="new-password"
                required
                minLength={PASSWORD_MIN_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 6 }}>
                At least {PASSWORD_MIN_LENGTH} characters. A few unrelated words is easier to
                remember, and harder to guess, than one word with symbols in it.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="confirm-password">Confirm new password</label>
              <input
                id="confirm-password"
                className="form-input"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" style={s.submitButton} type="submit" disabled={busy}>
              {busy ? 'Saving…' : copy.submit}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
